import { useMemo } from "react"
import ViewSheet from "@/v2-backoffice-client/components/viewSheet"
import AppViewSheetBreadcrumb from "@/v2-backoffice-client/app/AppViewSheetBreadcrumb"
import Tabs from "@/v2-backoffice-client/components/tabs"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import FeatureNotice from "@/v2-backoffice-client/feature/FeatureNotice"
import LicenseCapabilityViewInfo from "@/v2-backoffice-client/license/changes/LicenseCapabilityViewInfo"
import LicenseCapabilityViewToolbar from "@/v2-backoffice-client/license/changes/LicenseCapabilityViewToolbar"
import LicenseChangeDetail from "@/v2-backoffice-client/license/changes/LicenseChangeDetail"
import LicenseDecisionForm from "@/v2-backoffice-client/license/changes/LicenseDecisionForm"
import { LicenseChangesContext } from "@/v2-backoffice-client/license/changes/license.changes.context"
import { useCapabilityDecisionWriter } from "@/v2-backoffice-client/license/changes/license.changes.decide.hooks"
import { useFeatureSurfaceChangesQuery } from "@/v2-backoffice-client/license/changes/license.changes.query"
import {
  capabilityDecisionSubjectOf,
  displayNameOf,
  type LicenseChange,
} from "@/v2-backoffice-client/license/changes/license.changes.utils"
import { useFeatureGroupsQuery } from "@/v2-backoffice-client/license/featureGroup/license.featureGroup.query"

/**
 * The feed row that stands for a capability: a moved capability has two
 * rows under one code, and the latest one says where it is now.
 */
function latestRowFor(changes: readonly LicenseChange[], code: string) {
  return changes
    .filter((change) => change.SurfaceCode === code)
    .sort((a, b) => b.ChangedAt.localeCompare(a.ChangedAt))[0]
}

// The capability panel (?v=capability:<code>). Fed by the same change feed
// the board renders, so board→panel opens from the Apollo cache, and keyed
// by the capability's code because that is what the decision is written
// against and what the console's dev toolbar deep-links by. Access, the
// one decision this board exists for, is the first tab: who has it, the
// two answers, and under Package-only the packages, the lock's reach and
// who would lose it. Details is the rest: what the capability is, where
// it lives, where it is defined, and the technical trail.
function LicenseCapabilityViewSheet() {
  const { id: code } = useAppViewSheetOpener()

  const { data, previousData, loading, error } = useFeatureSurfaceChangesQuery({
    skip: !code,
  })
  const { data: packagesData } = useFeatureGroupsQuery({ skip: !code })
  // previousData keeps the panel rendered through post-save evictions,
  // when the cache is briefly empty and data undefined.
  const changes = (data ?? previousData)?.getFeatureSurfaceChanges ?? []
  const change = code ? latestRowFor(changes, code) : undefined
  const packages = useMemo(
    () => packagesData?.getFeatureGroups ?? [],
    [packagesData?.getFeatureGroups],
  )

  const { write, isSaving } = useCapabilityDecisionWriter()

  return (
    <LicenseChangesContext value={{ packages }}>
      <ViewSheet.Header>
        <AppViewSheetBreadcrumb />
        <ViewSheet.Title isLoading={loading && !change}>
          {change ? displayNameOf(change) : undefined}
        </ViewSheet.Title>
      </ViewSheet.Header>

      <ViewSheet.HeaderRow>
        <LicenseCapabilityViewInfo
          change={change}
          isLoading={loading && !change}
        />
        <LicenseCapabilityViewToolbar change={change} />
      </ViewSheet.HeaderRow>

      <Tabs
        id="license-capability-view-tabs"
        defaultValue="access"
        // Only gate the tabs while there is nothing to show: with
        // cache-and-network the feed reports loading on every revalidation
        // (a save evicts it), and gating on that would flash the panel.
        isLoading={loading && !change}
        error={error}
        isEmpty={!loading && !change}
        emptyLabel="Capability not found. It may have left the feed; the Features page still lists its code."
      >
        <Tabs.List>
          <Tabs.Trigger value="access" iconSrc="key">
            Access
          </Tabs.Trigger>
          <Tabs.Trigger value="details" iconSrc="info">
            Details
          </Tabs.Trigger>
        </Tabs.List>

        {/* flex-1 and no overflow-auto: the form scrolls its own middle and
            keeps the Save in reach at the foot of the tab. */}
        <Tabs.Content value="access" className="min-h-0 flex-1 p-1">
          {change &&
            (change.IsInCatalog ? (
              <LicenseDecisionForm
                // A save or a switch of row starts the draft afresh from
                // what is saved.
                key={`${change.SurfaceCode}:${change.FeatureAvailabilityTypeCode}:${change.FeatureGroupIds ?? ""}:${change.FreeConfirmedAt ?? ""}`}
                subject={capabilityDecisionSubjectOf(change)}
                packages={packages}
                isSaving={isSaving}
                onDecide={(decision, packageIds) =>
                  write(change.SurfaceCode, decision, packageIds)
                }
              />
            ) : (
              <FeatureNotice tone="info">
                This capability is gone from Fusion, so there is nothing left to
                decide. Any package that included it keeps the row until a
                developer cleans it up.
              </FeatureNotice>
            ))}
        </Tabs.Content>

        <Tabs.Content value="details" className="min-h-0 overflow-auto p-1">
          {change && <LicenseChangeDetail change={change} />}
        </Tabs.Content>
      </Tabs>
    </LicenseChangesContext>
  )
}

export default LicenseCapabilityViewSheet
